import { finalize, tap } from 'rxjs';
import debounce from 'lodash/debounce';
import type { ComputedRef, Ref, WritableComputedRef } from 'vue';

import { useToastStore } from '~/store/toast';

/** Marker property used to distinguish an account group-header row from a store row. */
export const ADMIN_STORE_GROUP_KEY = '__accountGroup';

export interface AdminStoreGroupHeader {
  [ADMIN_STORE_GROUP_KEY]: true;
  id: string;
  accountId: string;
  label: string;
  /**
   * The Parent Account name when this account belongs to a reseller (ENG-5850 Surface D). Rendered
   * as lineage on the existing header — `RedPulse › Fastlane` — rather than a third disclosure tier,
   * which would roughly triple the rendered rows and straddle page boundaries.
   */
  parentLabel?: string;
  /** Stores under this account — the server-true total when available, else the current page's rows. */
  count: number;
  /** During a search, how many of `count` matched — renders as "n of m" (ENG-5850 design 1s/1u). */
  matchedCount?: number;
  /** Whether this account's stores are currently hidden. */
  collapsed: boolean;
}

/**
 * How the table groups store rows (ENG-5850 Surface D).
 *
 * Replaces the old boolean "hide account name": selecting `parent-account` swaps the group *key*
 * rather than stacking tiers, so the row model and the pagination maths are untouched.
 */
export const GROUP_BY_OPTIONS = [
  { label: 'None', value: 'none' },
  { label: 'Child Account', value: 'child-account' },
  { label: 'Parent Account', value: 'parent-account' },
] as const;

export type AdminStoreGroupBy = (typeof GROUP_BY_OPTIONS)[number]['value'];

export interface AdminStoreAccountSummary {
  name: string;
  parentAccountId: string | null;
}

export function isAdminStoreGroupHeader(item: unknown): item is AdminStoreGroupHeader {
  return typeof item === 'object' && item !== null && (item as Record<string, unknown>)[ADMIN_STORE_GROUP_KEY] === true;
}

/** Platform id → display label for the Stores table's Platform column (ENG-5850 design). */
const PLATFORM_LABELS: Record<string, string> = {
  api: 'API',
  bigcommerce: 'BigCommerce',
  magento: 'Magento',
  shopify: 'Shopify',
};

export const platformLabel = (platform?: string | null): string => (platform && PLATFORM_LABELS[platform]) || '';

export type StoreStatusBadge = { label: string; type: 'success' | 'warning' | 'primary' | 'neutral' };

/** Store lifecycle state → status badge for the Stores table (ENG-5850 design: Active = success). */
const STORE_STATUS_BADGES: Record<string, StoreStatusBadge> = {
  ACTIVE: { label: 'Active', type: 'success' },
  DEVELOPMENT: { label: 'Development', type: 'neutral' },
  INACTIVE: { label: 'Inactive', type: 'neutral' },
  ONBOARDING: { label: 'Onboarding', type: 'warning' },
};

export const storeStatusBadge = (storeType?: string | null): StoreStatusBadge =>
  STORE_STATUS_BADGES[(storeType ?? '').toUpperCase()] ?? { label: '', type: 'neutral' };

/** Which of the three searchable entities a store row matched (ENG-5850 design 1s). */
export enum AdminStoreMatchType {
  BRAND = 'brand',
  CHILD = 'child',
  PARENT = 'parent',
}

export interface AdminStoreHighlightSegment {
  text: string;
  match: boolean;
}

const containsQuery = (value: unknown, needle: string) =>
  typeof value === 'string' && value.toLowerCase().includes(needle.toLowerCase());

/** Which denormalized field a store row matched; Brand outranks lineage so a direct hit is never masked. */
export function classifyAdminStoreMatch(
  store: { name?: string; accountName?: string | null; parentAccountName?: string | null } | null | undefined,
  query: string,
): AdminStoreMatchType | undefined {
  const needle = query.trim();
  if (!needle || !store) return undefined;
  if (containsQuery(store.name, needle)) return AdminStoreMatchType.BRAND;
  if (containsQuery(store.accountName, needle)) return AdminStoreMatchType.CHILD;
  if (containsQuery(store.parentAccountName, needle)) return AdminStoreMatchType.PARENT;

  return undefined;
}

/** Split text into segments for case-insensitive substring highlighting, preserving original casing. */
export function searchHighlightSegments(text: string, query: string): AdminStoreHighlightSegment[] {
  const needle = query.trim().toLowerCase();
  if (!text) return [];
  if (!needle) return [{ match: false, text }];

  const lower = text.toLowerCase();
  const segments: AdminStoreHighlightSegment[] = [];
  let cursor = 0;

  for (let hit = lower.indexOf(needle); hit !== -1; hit = lower.indexOf(needle, cursor)) {
    if (hit > cursor) segments.push({ match: false, text: text.slice(cursor, hit) });
    segments.push({ match: true, text: text.slice(hit, hit + needle.length) });
    cursor = hit + needle.length;
  }

  if (cursor < text.length) segments.push({ match: false, text: text.slice(cursor) });

  return segments;
}

/**
 * View preferences are held at module scope, keyed by tab (`active`, `onboarding`, ...), so they
 * survive tab switches within a session:
 *   - collapsedByTab[tab][accountId] === true → that one account's stores are hidden (chevron)
 *   - groupByTab[tab]                         → how rows are grouped, persisted across refreshes
 *
 * The stored VALUE changed shape (boolean → group-by enum) but the KEY deliberately has not — reusing
 * it is what lets `readGroupByPrefs` migrate an existing preference instead of resetting it.
 */
export const GROUP_BY_STORAGE_KEY = 'admin-stores:hide-account-name';

/**
 * Read the persisted group-by, migrating the old boolean-per-tab shape.
 *
 * The key previously stored `true` meaning "account rollup off"; that is now `'none'`, and `false`
 * (or absent) is the default `'child-account'`. Migrating in place means an existing user's stored
 * preference survives the change rather than silently resetting.
 */
export function readGroupByPrefs(): Record<string, AdminStoreGroupBy> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = JSON.parse(window.localStorage.getItem(GROUP_BY_STORAGE_KEY) ?? '{}');
    const valid = GROUP_BY_OPTIONS.map(option => option.value) as string[];

    return Object.fromEntries(
      Object.entries(raw as Record<string, unknown>).map(([tab, value]) => {
        if (typeof value === 'boolean') return [tab, value ? 'none' : 'child-account'];

        return [tab, valid.includes(value as string) ? (value as AdminStoreGroupBy) : 'child-account'];
      }),
    );
  } catch {
    return {};
  }
}

/** Server-computed per-account store counts from `GET /v1/admin/stores` (page-local size undercounts across page boundaries). */
export interface AdminStoreGroupCounts {
  /** accountId → total stores under the current filter (from the latest unsearched load). */
  total?: Record<string, number>;
  /** accountId → stores matching the active search; presence marks a search as active. */
  matched?: Record<string, number>;
}

/** Sum the per-account counts rolled up onto a group key — the account itself, or its parent tree. */
function sumCountsForGroup(
  key: string,
  byAccount: Record<string, number> | undefined,
  accounts: Map<string, AdminStoreAccountSummary>,
  byParent: boolean,
): number | undefined {
  if (!byAccount) return undefined;
  if (!byParent) return byAccount[key];

  let sum: number | undefined;
  for (const [accountId, count] of Object.entries(byAccount)) {
    const groupKey = accounts.get(accountId)?.parentAccountId ?? accountId;
    if (groupKey === key) sum = (sum ?? 0) + count;
  }

  return sum;
}

/**
 * Build the exact flat row model consumed by the admin store tables.
 *
 * Kept outside the composable so the migration-sensitive grouping rules can be exercised directly
 * without replacing Nuxt's gateway and pagination layers in a unit test. `groupedItems` below calls
 * this function, so the tests and production table always execute the same implementation.
 */
export function groupAdminStoreItems(
  stores: any[],
  accounts: Map<string, AdminStoreAccountSummary>,
  groupBy: AdminStoreGroupBy,
  collapsed: Record<string, boolean>,
  counts?: AdminStoreGroupCounts,
) {
  if (groupBy === 'none') return stores;

  const byParent = groupBy === 'parent-account';
  const groups = new Map<string, any[]>();

  for (const store of stores) {
    const ownAccountId = store?.accountId ?? 'unknown';
    // Grouping by Parent Account swaps the group KEY — it does not add a tier. An account with no
    // parent groups under itself, which is nearly every account, so those rows look untouched.
    const key = byParent ? (accounts.get(ownAccountId)?.parentAccountId ?? ownAccountId) : ownAccountId;

    const existing = groups.get(key);
    if (existing) {
      existing.push(store);
      continue;
    }
    groups.set(key, [store]);
  }

  const rows: any[] = [];
  for (const [accountId, groupStores] of groups) {
    const first = groupStores[0];
    const account = accounts.get(accountId);
    const name = account?.name ?? first?.account?.name ?? first?.accountName;
    const label = name ?? (accountId === 'unknown' ? 'Unassigned' : accountId);
    const isCollapsed = !!collapsed[accountId];

    // Lineage only reads as lineage when the header is the child: grouping BY parent already puts
    // the reseller's name in the label, so repeating it there would be noise.
    const parentId = byParent ? undefined : account?.parentAccountId;

    // Prefer server totals; "n of m" needs both, so a missing total falls back to the matched count.
    const total = sumCountsForGroup(accountId, counts?.total, accounts, byParent);
    const matched = counts?.matched
      ? (sumCountsForGroup(accountId, counts.matched, accounts, byParent) ?? groupStores.length)
      : undefined;

    rows.push({
      [ADMIN_STORE_GROUP_KEY]: true,
      accountId,
      collapsed: isCollapsed,
      count: total ?? matched ?? groupStores.length,
      id: `group-${accountId}`,
      label,
      matchedCount: total != null && matched != null ? matched : undefined,
      parentLabel: parentId ? (accounts.get(parentId)?.name ?? parentId) : undefined,
    });

    if (!isCollapsed) rows.push(...groupStores);
  }

  return rows;
}

/** Result-scope summary for the active search — "12 Brands · 3 Child Accounts · under RedPulse" (design 1s). */
export interface AdminStoreSearchMeta {
  query: string;
  /** Match types present on the current page, for the Brand/Child/Parent badges. */
  matchTypes: AdminStoreMatchType[];
  /** Matching stores across ALL pages (server counts), not just the loaded page. */
  brandCount: number;
  /** Accounts with at least one matching store. */
  childAccountCount: number;
  scopeLine: string;
  /** Set when the query itself hit a Parent Account name — drives the 1s banner + hand-off link. */
  parentMatch?: { accountId?: string; name: string };
}

const pluralize = (count: number, singular: string) => `${count} ${count === 1 ? singular : `${singular}s`}`;

/** Derive the search summary client-side from the page rows plus server counts (the paginator returns no match metadata). */
export function buildAdminStoreSearchMeta(
  query: string,
  stores: any[],
  accounts: Map<string, AdminStoreAccountSummary>,
  matchedCounts: Record<string, number>,
): AdminStoreSearchMeta | null {
  const needle = query.trim();
  if (!needle) return null;

  const matchTypes = [
    ...new Set(
      stores.map(store => classifyAdminStoreMatch(store, needle)).filter((type): type is AdminStoreMatchType => !!type),
    ),
  ];

  const countValues = Object.values(matchedCounts);
  const brandCount = countValues.length ? countValues.reduce((sum, count) => sum + count, 0) : stores.length;
  const childAccountCount = countValues.length
    ? countValues.length
    : new Set(stores.map(store => store?.accountId).filter(Boolean)).size;

  const parentNames = new Set(stores.map(store => store?.parentAccountName).filter(Boolean) as string[]);
  const underParentName = parentNames.size === 1 ? [...parentNames][0] : undefined;

  const parentRow = stores.find(store => containsQuery(store?.parentAccountName, needle));
  const parentMatch = parentRow
    ? { accountId: accounts.get(parentRow.accountId)?.parentAccountId ?? undefined, name: parentRow.parentAccountName }
    : undefined;

  // Scope line varies by match type: Parent names the tree, Child drops the count, Brand spells out both.
  const isParentMatch = matchTypes.includes(AdminStoreMatchType.PARENT);
  const isChildMatch = !isParentMatch && matchTypes.includes(AdminStoreMatchType.CHILD);
  const parts = [pluralize(brandCount, 'Brand')];

  if (isParentMatch) {
    parts.push(pluralize(childAccountCount, 'Child Account'));
  } else if (isChildMatch) {
    if (underParentName) parts.push(`under ${underParentName}`);
  } else {
    parts.push(`in ${pluralize(childAccountCount, 'Child Account')}`);
    if (underParentName) parts.push(`under ${underParentName}`);
  }

  return { brandCount, childAccountCount, matchTypes, parentMatch, query: needle, scopeLine: parts.join(' · ') };
}

const collapsedByTab = reactive<Record<string, Record<string, boolean>>>({});
const groupByTab = reactive<Record<string, AdminStoreGroupBy>>(readGroupByPrefs());

/**
 * Search meta published per tab at module scope: the search input (and its scope line/banner) lives
 * in the parent `admin/stores.vue` page while the data lives in each tab's composable instance.
 */
export const adminStoreSearchMetaByTab = reactive<Record<string, AdminStoreSearchMeta | null>>({});

export interface AdminStoreControls {
  groupBy: WritableComputedRef<AdminStoreGroupBy>;
  parentAccountFilter: Ref<string>;
  parentAccountOptions: ComputedRef<Array<{ label: string; value: string }>>;
}

/**
 * The active tab publishes its grouping controls here so the page shell can host them in ONE toolbar
 * beside the search — rather than the sub-page cramming them into the table's actions header, where
 * they overflowed and bunched against the Parent Account filter (ENG-5850 design 1s).
 * shallowReactive so an assignment (a tab mounting) is tracked, while the stored refs stay raw.
 */
export const adminStoreControlsByTab = shallowReactive<Record<string, AdminStoreControls>>({});

function collapsedMapFor(tab: string) {
  if (!collapsedByTab[tab]) collapsedByTab[tab] = {};

  return collapsedByTab[tab];
}

export default function useAdminStores(state: string) {
  const pagination = usePagination(`admin/stores/${state}`);
  const stores = ref<any[]>([]);
  const loading = ref(false);
  const currentSearch = ref('');
  const { merchant, identity } = useGateway();
  const toast = useToastStore();

  /** accountId → name + lineage, from identity-svc, so headers show a name instead of a cuid. */
  const accounts = ref<Map<string, AdminStoreAccountSummary>>(new Map());
  const collapsed = collapsedMapFor(state);

  /** Server per-account counts for the CURRENT view — search-honoring while a search is active. */
  const accountCounts = ref<Record<string, number>>({});
  /** Server per-account counts from the latest UNSEARCHED load — the "m" in a search's "n of m". */
  const baselineAccountCounts = ref<Record<string, number>>({});

  /** How rows are grouped: none (flat), by Child Account, or by Parent Account. */
  const groupBy = computed<AdminStoreGroupBy>({
    get: () => groupByTab[state] ?? 'child-account',
    set: value => {
      groupByTab[state] = value;
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(GROUP_BY_STORAGE_KEY, JSON.stringify(groupByTab));
      }
    },
  });

  /**
   * Scope the table to one Parent Account, server-side.
   *
   * The paginator merges any non-`search` filter straight into the Prisma `where`, so this needs no
   * backend change — and because the server does the counting, the row count stays correct.
   */
  const parentAccountFilter = ref<string>('');

  /** Every account that has children — the options for the Parent Account filter. */
  const parentAccountOptions = computed(() => {
    const parentIds = new Set(
      [...accounts.value.values()].map(account => account.parentAccountId).filter((id): id is string => !!id),
    );

    return [...parentIds]
      .map(id => ({ label: accounts.value.get(id)?.name ?? id, value: id }))
      .sort((a, b) => a.label.localeCompare(b.label));
  });

  // Publish this tab's controls so the page shell can host them in its toolbar beside the search.
  adminStoreControlsByTab[state] = { groupBy, parentAccountFilter, parentAccountOptions };

  /** The account ids in a Parent Account's tree — the parent itself plus its children. */
  function accountIdsUnder(parentId: string) {
    const children = [...accounts.value.entries()]
      .filter(([, account]) => account.parentAccountId === parentId)
      .map(([id]) => id);

    return [parentId, ...children];
  }

  function toggleAccount(accountId: string) {
    collapsed[accountId] = !collapsed[accountId];
  }

  /**
   * Stores grouped under their account, as a flat list of rows suitable for the table: each account
   * contributes one group-header row followed by its store rows (unless that account is collapsed).
   *
   * With `groupBy === 'none'` we skip grouping and return the stores as-is. Grouping otherwise
   * applies to the current page's results only (the list is server-paginated), preserving the
   * first-appearance order of accounts and the existing order within each account. The header label
   * prefers the account name (from identity-svc), falling back to the accountId.
   */
  const groupedItems = computed(() => {
    const searching = !!currentSearch.value.trim();

    return groupAdminStoreItems(stores.value, accounts.value, groupBy.value, collapsed, {
      matched: searching ? accountCounts.value : undefined,
      total: searching ? baselineAccountCounts.value : accountCounts.value,
    });
  });

  const searchMeta = computed(() =>
    buildAdminStoreSearchMeta(currentSearch.value, stores.value, accounts.value, accountCounts.value),
  );

  // Publish for the parent page (which owns the search input); hold the last meta while a new
  // search is in flight so the scope line doesn't flicker against stale rows.
  watch(
    [searchMeta, loading],
    ([meta, isLoading]) => {
      if (!isLoading) adminStoreSearchMetaByTab[state] = meta;
    },
    { immediate: true },
  );

  function loadAccountNames() {
    return identity.accounts
      .list()
      .pipe(
        tap(res => {
          const map = new Map<string, AdminStoreAccountSummary>();
          for (const account of res.data ?? []) {
            map.set(account.id, { name: account.name, parentAccountId: account.parentAccountId ?? null });
          }
          accounts.value = map;
        }),
      )
      .subscribe({
        error: () =>
          toast.addToast({
            id: `admin-stores:${state}:account-names-error`,
            message: 'Failed to load account names. Grouping and Parent Account filters may be incomplete.',
            timeout: 5000,
            type: 'error',
          }),
      });
  }

  const handleStoreLoadError = () =>
    toast.addToast({
      id: `admin-stores:${state}:load-error`,
      message: 'Failed to load stores. Please try again.',
      timeout: 5000,
      type: 'error',
    });

  /** The server-side `where` fragment for the Parent Account filter, or nothing when unset. */
  function parentAccountScopeFilter() {
    if (!parentAccountFilter.value) return {};

    return { accountId: { in: accountIdsUnder(parentAccountFilter.value) } };
  }

  function loadStores() {
    loading.value = true;

    return merchant.stores.admin
      .list({
        filter: {
          storeType: state.toUpperCase(),
          ...parentAccountScopeFilter(),
        },
        ...pagination.args.value,
        include: ['generalSettings', 'storeSettings', 'capabilities'],
        orderBy: 'name',
        reverse: true,
      })
      .pipe(
        finalize(() => (loading.value = false)),
        tap(res => {
          pagination.setCount(res.counts?.[state] ?? 0);
          pagination.setPageInformationFromResponse(res.pageInfo!);
          stores.value = res.data.edges.map(edge => edge.node);
          accountCounts.value = res.accountCounts ?? {};
          baselineAccountCounts.value = res.accountCounts ?? {};
        }),
      )
      .subscribe({ error: handleStoreLoadError });
  }

  function loadSearchedStores(searchQuery: string) {
    loading.value = true;

    const params = {
      filter: {
        search: searchQuery,
        storeType: state.toUpperCase(),
        ...parentAccountScopeFilter(),
      },
      ...pagination.args.value,
      include: ['generalSettings', 'storeSettings', 'capabilities'],
      mode: 'insensitive',
      orderBy: 'name',
      reverse: true,
    };

    return merchant.stores.admin
      .list(params)
      .pipe(
        finalize(() => (loading.value = false)),
        tap(res => {
          pagination.setCount(res.counts?.[state] ?? 0);
          pagination.setPageInformationFromResponse(res.pageInfo!);
          stores.value = res.data.edges.map(edge => edge.node);
          accountCounts.value = res.accountCounts ?? {};
        }),
      )
      .subscribe({ error: handleStoreLoadError });
  }

  const searchStores = debounce((searchQuery: string) => {
    currentSearch.value = searchQuery;

    return searchQuery.trim() ? loadSearchedStores(searchQuery) : loadStores();
  }, 300);

  /** Reload the current view without silently dropping the search term shown in the page header. */
  const reloadCurrentView = () => (currentSearch.value.trim() ? loadSearchedStores(currentSearch.value) : loadStores());

  // Fire-and-forget: dispose if the tab unmounts before the response lands.
  let accountNamesSub: ReturnType<typeof loadAccountNames> | undefined;

  onMounted(() => {
    loadStores();
    accountNamesSub = loadAccountNames();
  });

  onUnmounted(() => {
    accountNamesSub?.unsubscribe();
    adminStoreSearchMetaByTab[state] = null;
  });

  watch(
    () => state,
    async () => {
      stores.value = [];

      loadStores();
    },
  );

  pagination.events.on(pagination.emits.CHANGE, reloadCurrentView);

  // Scoping to a Parent Account changes both the result set and its cursors. Reset to page one, then
  // re-run the visible search instead of leaving a stale search box over an unsearched response.
  watch(parentAccountFilter, async () => {
    await pagination.reset();
    reloadCurrentView();
  });

  return {
    accountCounts,
    accounts,
    groupBy,
    groupedItems,
    load: loadStores,
    loading,
    pagination,
    parentAccountFilter,
    parentAccountOptions,
    search: searchStores,
    searchMeta,
    stores,
    toggleAccount,
  };
}
